import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    doc,
    getDoc,
    updateDoc,
    Timestamp,
    orderBy
} from 'firebase/firestore';
import { db } from './firebase';

// サブドメインの登録
export const registerSubdomain = async (subdomain, accountId, userEmail) => {
    try {
        // サブドメインの形式をチェック
        if (!isValidSubdomain(subdomain)) {
            throw new Error('サブドメインは英数字とハイフンのみ使用可能です（3-63文字）');
        }

        // サブドメインが既に使用されているかチェック
        try {
            const existingSubdomain = await getSubdomainBySubdomain(subdomain);
            if (existingSubdomain) {
                throw new Error('このサブドメインは既に使用されています');
            }
        } catch (error) {
            // Firestore権限エラーの場合は、サブドメインが存在しないとみなす
            if (error.code === 'permission-denied') {
                console.warn('サブドメイン権限チェックでエラーが発生しました:', error);
            } else {
                throw error;
            }
        }

        // アカウントが既にサブドメインを持っているかチェック
        const existingAccountSubdomain = await getSubdomainByAccountId(accountId);
        if (existingAccountSubdomain) {
            throw new Error('既にサブドメインが登録されています');
        }

        // サブドメインを登録
        const subdomainData = {
            subdomain: subdomain.toLowerCase(),
            accountId: accountId,
            userEmail: userEmail,
            status: 'active',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };

        const docRef = await addDoc(collection(db, 'subdomains'), subdomainData);

        // Firebase Functionsのトリガーにより、自動的にFirebase Hostingサイトが作成される
        // onSubdomainCreated関数が実行される

        return {
            id: docRef.id,
            ...subdomainData
        };
    } catch (error) {
        console.error('サブドメイン登録エラー:', error);
        throw error;
    }
};

// サブドメインでユーザー情報を取得
export const getSubdomainBySubdomain = async (subdomain) => {
    try {
        const q = query(
            collection(db, 'subdomains'),
            where('subdomain', '==', subdomain.toLowerCase()),
            where('status', '==', 'active')
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const doc = querySnapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data()
        };
    } catch (error) {
        console.error('サブドメイン取得エラー:', error);
        // 権限エラーの場合はnullを返す（サブドメインが存在しないとみなす）
        if (error.code === 'permission-denied') {
            console.warn('サブドメイン権限チェックでエラーが発生しました:', error);
            return null;
        }
        throw error;
    }
};

// アカウントIDでサブドメイン情報を取得
export const getSubdomainByAccountId = async (accountId) => {
    try {
        const q = query(
            collection(db, 'subdomains'),
            where('accountId', '==', accountId),
            where('status', '==', 'active')
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const doc = querySnapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data()
        };
    } catch (error) {
        console.error('アカウントサブドメイン取得エラー:', error);
        // 権限エラーの場合はnullを返す
        if (error.code === 'permission-denied') {
            console.warn('アカウントサブドメイン権限チェックでエラーが発生しました:', error);
            return null;
        }
        throw error;
    }
};

// ユーザーIDでサブドメイン情報を取得（後方互換性のため残す）
export const getSubdomainByUserId = async (userId) => {
    try {
        const q = query(
            collection(db, 'subdomains'),
            where('accountId', '==', userId), // accountIdとして検索
            where('status', '==', 'active')
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        const doc = querySnapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data()
        };
    } catch (error) {
        console.error('ユーザーサブドメイン取得エラー:', error);
        // 権限エラーの場合はnullを返す
        if (error.code === 'permission-denied') {
            console.warn('ユーザーサブドメイン権限チェックでエラーが発生しました:', error);
            return null;
        }
        throw error;
    }
};

// サブドメインの更新
export const updateSubdomain = async (subdomainId, updates) => {
    try {
        const docRef = doc(db, 'subdomains', subdomainId);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: Timestamp.now()
        });

        return true;
    } catch (error) {
        console.error('サブドメイン更新エラー:', error);
        throw error;
    }
};

// サブドメインの形式をチェック
export const isValidSubdomain = (subdomain) => {
    // 3-63文字の英数字とハイフンのみ
    const subdomainRegex = /^[a-z0-9-]{3,63}$/;

    // ハイフンで始まらない、終わらない
    if (subdomain.startsWith('-') || subdomain.endsWith('-')) {
        return false;
    }

    // 連続するハイフンがない
    if (subdomain.includes('--')) {
        return false;
    }

    return subdomainRegex.test(subdomain.toLowerCase());
};

// 現在のホスト名からサブドメインを抽出
export const extractSubdomainFromHost = (hostname) => {
    console.log('🌐 ホスト名解析:', hostname);

    // ローカル開発環境の処理
    if (hostname.includes('localhost')) {
        const parts = hostname.split('.');

        // test01.localhost のような形式の場合
        if (parts.length > 1 && parts[parts.length - 1] === 'localhost') {
            const subdomain = parts[0];
            console.log('🔧 ローカルサブドメイン:', subdomain);
            return subdomain;
        }

        // localhost の場合はURLパラメータからサブドメインを取得
        if (hostname === 'localhost') {
            const urlParams = new URLSearchParams(window.location.search);
            const subdomain = urlParams.get('subdomain');
            console.log('🔧 URLパラメータサブドメイン:', subdomain);
            return subdomain || null;
        }
    }

    // Vercelのデフォルトドメインの場合
    if (hostname.includes('vercel.app') || hostname.includes('vercel-dns.com')) {
        console.log('⚡ Vercelドメイン検出');
        const urlParams = new URLSearchParams(window.location.search);
        const subdomain = urlParams.get('subdomain');
        return subdomain || null;
    }

    // myshop.suisui.life のような本番環境の場合（Cloudflare経由）
    const parts = hostname.split('.');
    console.log('🔍 ドメイン部分:', parts);

    if (parts.length >= 3) {
        // suisui.life の場合、最初の部分がサブドメイン
        if (parts[parts.length - 2] === 'suisui' && parts[parts.length - 1] === 'life') {
            const subdomain = parts[0];
            console.log('✅ サブドメイン検出:', subdomain);
            return subdomain; // myshop
        }
    }

    // その他の場合（Firebase Hostingのデフォルトドメインなど）
    // URLパラメータからサブドメインを取得
    const urlParams = new URLSearchParams(window.location.search);
    const subdomain = urlParams.get('subdomain');
    console.log('🔧 URLパラメータサブドメイン:', subdomain);
    return subdomain || null;
};

// サブドメインの利用可能性をチェック
export const checkSubdomainAvailability = async (subdomain) => {
    try {
        const existingSubdomain = await getSubdomainBySubdomain(subdomain);
        return !existingSubdomain;
    } catch (error) {
        console.error('サブドメイン利用可能性チェックエラー:', error);
        throw error;
    }
};

// サブドメイン登録時に管理者に通知する関数
export const notifySubdomainRegistration = async (subdomain, accountId, userEmail) => {
    try {
        // 管理者通知用のコレクションに記録
        const notificationData = {
            subdomain: subdomain.toLowerCase(),
            accountId: accountId,
            userEmail: userEmail,
            status: 'pending', // pending, approved, rejected
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            message: `新しいサブドメイン「${subdomain}.suisui.life」が登録されました。Firebase Hostingに手動で追加してください。`
        };

        const docRef = await addDoc(collection(db, 'subdomainNotifications'), notificationData);

        console.log(`サブドメイン登録通知が作成されました: ${docRef.id}`);

        return {
            id: docRef.id,
            ...notificationData
        };
    } catch (error) {
        console.error('サブドメイン登録通知エラー:', error);
        throw error;
    }
};

// 管理者用：サブドメイン通知一覧を取得
export const getSubdomainNotifications = async () => {
    try {
        const q = query(
            collection(db, 'subdomainNotifications'),
            orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const notifications = [];

        querySnapshot.forEach((doc) => {
            notifications.push({
                id: doc.id,
                ...doc.data()
            });
        });

        return notifications;
    } catch (error) {
        console.error('サブドメイン通知取得エラー:', error);
        throw error;
    }
};

// 管理者用：サブドメイン通知のステータスを更新
export const updateSubdomainNotificationStatus = async (notificationId, status, adminNotes = '') => {
    try {
        const docRef = doc(db, 'subdomainNotifications', notificationId);
        await updateDoc(docRef, {
            status: status,
            adminNotes: adminNotes,
            updatedAt: Timestamp.now()
        });

        console.log(`サブドメイン通知ステータスが更新されました: ${notificationId} -> ${status}`);
    } catch (error) {
        console.error('サブドメイン通知更新エラー:', error);
        throw error;
    }
}; 